import React, { useCallback } from 'react';
import type { FC, ReactNode } from 'react';
import { match } from 'variant';
import { useDebugLog } from '@wix/cli-debug-log';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { APP_CONFIG_PATH } from '@wix/cli-app-definitions';
import { useAsync, useExit, Spinner } from '@wix/cli-ui-kit';
import { useSiteListClient } from '@wix/site-list-client';
import type { Site } from '@wix/site-list-client';
import { useTranslation } from '@wix/cli-i18n';
import { saveAppConfig } from '../files/app.config.js';
import { useProjectModel } from '../model.js';
import { ResolveSite } from './ResolveSite.js';
import { SiteContext } from './context.js';
import { SiteState } from './site-state.js';
import { resolveConfiguredSite } from './resolve-configured-site.js';

export const SiteProvider: FC<{
  siteId?: string;
  children: ReactNode | ((site: Site) => ReactNode);
}> = ({ children, siteId }) => {
  const exit = useExit();
  const {
    model: { projectFolder },
  } = useProjectModel();
  const writeDebugLog = useDebugLog();
  const siteListClient = useSiteListClient();
  const { t } = useTranslation();

  const { status, merge } = useAsync(
    async () =>
      resolveConfiguredSite({
        projectFolder,
        siteListClient,
        siteId,
        writeDebugLog,
      }),
    []
  );

  const setSite = useCallback(
    async (site: Site) => {
      try {
        await saveAppConfig(projectFolder, { siteId: site.id });
        merge({ result: SiteState.Resolved({ site }) });
      } catch (e) {
        throw new CliError({
          code: CliErrorCode.FailedToSaveDevelopmentSite({
            path: APP_CONFIG_PATH,
          }),
          cause: e,
        });
      }
    },
    [merge, projectFolder]
  );

  const handleResolveSite = useCallback(
    (site: Site) => setSite(site).catch(exit),
    [exit, setSite]
  );

  return match(status, {
    Error: () => null,
    Loading: () => <Spinner text={t('dev_command.site_provider.loading')} />,
    Success: ({ result: state }) => {
      return match(state, {
        NotConfigured: (variant) => (
          <ResolveSite state={variant} onSubmit={handleResolveSite} />
        ),
        NotFound: (variant) => (
          <ResolveSite state={variant} onSubmit={handleResolveSite} />
        ),
        Resolved: ({ site }) => (
          <SiteContext.Provider
            value={{
              site,
              setSite,
            }}
          >
            {typeof children === 'function' ? children(site) : children}
          </SiteContext.Provider>
        ),
      });
    },
  });
};
